import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { Link, useNavigate } from "react-router-dom";

export default function TestimonialList() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // ✅ 3 views
  const [view, setView] = useState("active"); // "active" | "inactive" | "brandInactive"

  const token = localStorage.getItem("profileToken");
  const baseURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const headers = { "x-access-token": token || "" };

  const fetchTestimonials = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.get(`${baseURL}/testimonial/list`, { headers });

      if (res.data.status) {
        setTestimonials(res.data.result || []);
      } else {
        setTestimonials([]);
        setMessage(res.data.message || "No data");
      }
    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    // eslint-disable-next-line
  }, []);

  // ✅ counts (IMPORTANT: active = status=1 + brand_status=1)
  const activeCount = useMemo(
    () =>
      testimonials.filter(
        (t) => Number(t.status) === 1 && Number(t.brand_status) === 1
      ).length,
    [testimonials]
  );

  const inactiveCount = useMemo(
    () => testimonials.filter((t) => Number(t.status) === 0).length,
    [testimonials]
  );

  const brandInactiveCount = useMemo(
    () =>
      testimonials.filter(
        (t) => Number(t.status) === 1 && Number(t.brand_status) === 0
      ).length,
    [testimonials]
  );

  // ✅ visible list
  const visibleTestimonials = useMemo(() => {
    if (view === "inactive") {
      return testimonials.filter((t) => Number(t.status) === 0);
    }
    if (view === "brandInactive") {
      return testimonials.filter(
        (t) => Number(t.status) === 1 && Number(t.brand_status) === 0
      );
    }
    // ✅ active view
    return testimonials.filter(
      (t) => Number(t.status) === 1 && Number(t.brand_status) === 1
    );
  }, [testimonials, view]);

  const handleToggleStatus = async (t) => {
    try {
      await axios.patch(`${baseURL}/testimonial/${t.id}/status`, {}, { headers });

      const newStatus = Number(t.status) === 1 ? 0 : 1;

      setTestimonials((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, status: newStatus } : x))
      );

      // ✅ view switch correctly after toggle
      if (newStatus === 0) setView("inactive");
      else setView(Number(t.brand_status) === 0 ? "brandInactive" : "active");

      Swal.fire("Success", "Status updated", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the testimonial permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    try {
      const res = await axios.delete(`${baseURL}/testimonial/delete/${id}`, { headers });

      if (res.data.status) {
        Swal.fire("Deleted", res.data.message || "Deleted", "success");
        fetchTestimonials();
      } else {
        Swal.fire("Error", res.data.message || "Failed", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete testimonial", "error");
    }
  };

  if (loading) return <p className="text-center mt-5">Loading testimonials...</p>;

  const heading =
    view === "inactive"
      ? "Inactive Testimonials"
      : view === "brandInactive"
      ? "Brand Inactive Testimonials"
      : "Testimonial List";

  const badgeCount =
    view === "inactive"
      ? inactiveCount
      : view === "brandInactive"
      ? brandInactiveCount
      : activeCount;

  return (
    <div className="container my-5 p-4 border rounded shadow bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0 text-success">
          {heading}
          <span className="badge bg-secondary ms-2">{badgeCount}</span>
        </h3>

        <div className="d-flex gap-2 flex-wrap">
          {/* ✅ Active/Inactive (personal) */}
          <button
            className={`btn ${
              view === "inactive" ? "btn-outline-success" : "btn-outline-danger"
            } rounded-pill px-4`}
            onClick={() => setView((p) => (p === "inactive" ? "active" : "inactive"))}
          >
            {view === "inactive"
              ? `Active (${activeCount})`
              : `Inactive (${inactiveCount})`}
          </button>

          {/* ✅ Brand Inactive */}
          <button
            className={`btn ${
              view === "brandInactive" ? "btn-warning" : "btn-outline-warning"
            } rounded-pill px-4`}
            onClick={() => setView((p) => (p === "brandInactive" ? "active" : "brandInactive"))}
          >
            Brand Inactive ({brandInactiveCount})
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-testimonial">
            <i className="fas fa-plus me-2"></i> Add Testimonial
          </Link>
        </div>
      </div>

      {/* ✅ Note + Go to inactive brands */}
      {view === "brandInactive" && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            These testimonials are <b>not visible on website</b> because their brands are inactive.
          </div>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => navigate("/brands-list?view=inactive")}
          >
            Go to Inactive Brands
          </button>
        </div>
      )}

      {message && <div className="alert alert-info text-center">{message}</div>}

      <table className="table table-striped table-bordered align-middle text-center">
        <thead className="table-success">
          <tr>
            <th>#</th>
            <th>Brand</th>
            <th>Logo</th>
            <th>Text</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {visibleTestimonials.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center py-4 text-muted">
                No testimonials found.
              </td>
            </tr>
          ) : (
            visibleTestimonials.map((t, index) => {
              const isBrandInactive = Number(t.brand_status) === 0;

              return (
                <tr
                  key={t.id}
                  className={view !== "active" ? "table-secondary" : ""}
                  style={view !== "active" ? { opacity: 0.92 } : {}}
                >
                  <td>{index + 1}</td>

                  <td style={{ textAlign: "left" }}>
                    <div className="fw-semibold">
                      {t.brand_name || "-"}
                      {isBrandInactive && (
                        <span className="badge bg-danger ms-2">Brand Inactive</span>
                      )}
                    </div>

                    {isBrandInactive && (
                      <div className="small text-danger mt-1">
                        For activate this testimonial you should activate <b>{t.brand_name || "this brand"}</b>.
                      </div>
                    )}
                  </td>

                  <td style={{ width: 120 }}>
                    {t.brand_logo ? (
                      <img
                        src={`${baseURL}/uploads/${t.brand_logo}`}
                        alt={t.brand_name || "Brand Logo"}
                        style={{ height: 40, objectFit: "contain" }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>

                  <td style={{ maxWidth: 450, textAlign: "left" }}>{t.text}</td>

                  <td>
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Number(t.status) === 1}
                        onChange={() => handleToggleStatus(t)}
                      />
                    </div>
                  </td>

                  <td>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      title="Edit"
                      onClick={() => navigate(`/update-testimonial/${t.id}`)}
                    >
                      <i className="fa fa-edit"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      title="Delete"
                      onClick={() => handleDelete(t.id)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
